import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, Empty, Space, Typography, DatePicker, Table, Button } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  PlayCircleOutlined,
  CalendarOutlined,
  TrophyOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { supabase } from '../lib/supabase';

dayjs.extend(isBetween);

const { Title } = Typography;

const { RangePicker } = DatePicker;

const users = {
  "0c5d4557-83b7-41b4-b4e0-3eef7dd22f81": "atinaz",
  "307c3229-3ee7-49be-910f-c0422ad64a20": "fdogan",
  "407641e3-e8c8-481c-b45b-7f4b48309d6d": "ssepil",
  "7f5aa598-9e68-46cb-8dd5-5718e6580d2a": "cpalasoglu",
  "e5bebbf1-ef6e-4ff7-9dba-e7a68d841b75": "elifkurt"
}
function Statistics() {
  const [statistics, setStatistics] = useState({
    totalToday: 0,
    successToday: 0,
    failedToday: 0,
    runningToday: 0,
    stoppedToday: 0,
    pendingToday: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);



  // Fetch executions and calculate statistics
  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
      const endDate = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
      
      const { data, error } = await supabase
        .from('execution')
        .select('*')
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      const stats = {
        totalToday: data.length,
        successToday: data.filter(e => e.status === 'completed').length,
        failedToday: data.filter(e => e.status === 'failed').length,
        runningToday: data.filter(e => e.status === 'running').length,
        stoppedToday: data.filter(e => e.status === 'stopped').length,
        pendingToday: data.filter(e => e.status === 'pending').length
      };
      
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async () => {
    setTableLoading(true);
    
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
      const endDate = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
      
      // Get execution data without user relation
        const { data, error } = await supabase
        .from('execution')
        .select('created_at, plate, policy_no, type, req_number, runned_by, status')
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      const formattedData = data.map((item, index) => ({
        key: index,
        plate: item.plate,
        belgeSeriNo: item.req_number || '',
        policyNo: item.policy_no ? item.policy_no.split('/')[0] : '',
        durum: item.status || 'N/A',
        type: item.type || 'N/A',
        sorumlu: users[item.runned_by] || item.runned_by || 'N/A',
        date: dayjs(item.created_at).format('DD/MM/YYYY HH:mm'),
        isOffer: item.type === 'Teklif'
      }));
      
      setTableData(formattedData);
    } catch (err) {
      console.error('Error fetching table data:', err);
      setError(`Failed to fetch table data: ${err.message}`);
    } finally {
      setTableLoading(false);
    }
  };

  const exportToExcel = () => {
    const exportData = tableData.map(item => ({
      'Tarih': item.date,
      'Plaka': item.plate,
      'Belge Seri No': item.belgeSeriNo,
      'Poliçe No': item.policyNo,
      'Durum': item.durum,
      'Poliçe Tipi': item.type,
      'Sorumlu': item.sorumlu
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Kolon genişliklerini ayarla
    const columnWidths = [
      { wch: 18 }, // Tarih
      { wch: 12 }, // Plaka
      { wch: 18 }, // Belge Seri No
      { wch: 20 }, // Poliçe No
      { wch: 12 }, // Durum
      { wch: 15 }, // Poliçe Tipi
      { wch: 15 }  // Sorumlu
    ];
    ws['!cols'] = columnWidths;
    
    // Başlık satırını yeşil yap
    const headerStyle = {
      fill: {
        fgColor: { rgb: "4CAF50" } // Yeşil renk
      },
      font: {
        color: { rgb: "FFFFFF" }, // Beyaz yazı
        bold: true
      }
    };
    
    // Başlık hücrelerine stil uygula (A1:G1)
    const headerCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1'];
    headerCells.forEach(cell => {
      if (!ws[cell]) ws[cell] = {};
      ws[cell].s = headerStyle;
    });
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Poliçeler');
    
    const fileName = `policeler_${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  useEffect(() => {
    fetchStatistics();
    fetchTableData();

    // Set up polling for updates every 30 seconds
    const interval = setInterval(() => {
      fetchStatistics();
      fetchTableData();
    }, 30000);

    return () => clearInterval(interval);
  }, [dateRange]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div>
            <p>Failed to load statistics</p>
            <small style={{ color: '#666' }}>{error}</small>
          </div>
        }
      />
    );
  }

  const successRate = statistics.totalToday > 0 
    ? ((statistics.successToday / statistics.totalToday) * 100).toFixed(1)
    : 0;

  return (
    <div className="statistics-container">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        
        {/* Header */}
        <div className="statistics-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                📊 Test Statistics
              </Title>
              <p style={{ color: '#666', marginTop: '8px' }}>
                {dateRange[0].format('MMMM DD, YYYY')} - {dateRange[1].format('MMMM DD, YYYY')}
              </p>
            </div>
            <div>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates) {
                    setDateRange([dates[0].startOf('day'), dates[1].endOf('day')]);
                  } else {
                    setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
                  }
                }}
                allowClear={false}
                format="YYYY-MM-DD"
              />
            </div>
          </div>
        </div>

        {/* Main Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="statistics-card total-card">
              <Statistic
                title="Total Tests"
                value={statistics.totalToday}
                prefix={<CalendarOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="statistics-card success-card">
              <Statistic
                title="Successful Tests"
                value={statistics.successToday}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
                suffix={statistics.totalToday > 0 ? `(${((statistics.successToday / statistics.totalToday) * 100).toFixed(1)}%)` : ''}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="statistics-card failure-card">
              <Statistic
                title="Failed Tests"
                value={statistics.failedToday}
                prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
                suffix={statistics.totalToday > 0 ? `(${((statistics.failedToday / statistics.totalToday) * 100).toFixed(1)}%)` : ''}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card hoverable className="statistics-card success-rate-card">
              <Statistic
                title="Success Rate"
                value={successRate}
                prefix={<TrophyOutlined style={{ color: '#fa8c16' }} />}
                suffix="%"
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Test Verileri Tablosu */}
        <Card 
          title="Poliçeler" 
          loading={tableLoading}
          extra={
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={tableData.length === 0}
            >
              Excel İndir
            </Button>
          }
        >
          <Table
            dataSource={tableData}
            columns={[

              {
                title: 'Tarih',
                dataIndex: 'date',
                key: 'date',
                width: 100,
              },
              
              {
                title: 'Plaka',
                dataIndex: 'plate',
                key: 'plate',
                width: 100,
              },
              {
                title: 'Belge Seri No',
                dataIndex: 'belgeSeriNo',
                key: 'belgeSeriNo',
                width: 120,
              },
              {
                title: 'Poliçe No',
                dataIndex: 'policyNo',
                key: 'policyNo',
                width: 130,
              },
              {
                title: 'Durum',
                dataIndex: 'durum',
                key: 'durum',
                width: 80,
              },
              {
                title: 'Poliçe Tipi',
                dataIndex: 'type',
                key: 'type',
                width: 80,
                render: (text, record) => (
                  <span style={{ color: record.isOffer ? '#ff4d4f' : 'inherit' }}>
                    {text}
                  </span>
                ),
              },
              {
                title: 'Sorumlu',
                dataIndex: 'sorumlu',
                key: 'sorumlu',
                width: 100,
              },
            ]}
            pagination={false}
            locale={{
              emptyText: 'Seçilen tarih aralığında veri bulunamadı'
            }}
          />
        </Card>

        {/* Empty state for no tests */}
        {statistics.totalToday === 0 && (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <p>No tests found for selected date range</p>
                  <small style={{ color: '#666' }}>Try selecting a different date range or start a new test execution</small>
                </div>
              }
            />
          </Card>
        )}

      </Space>
    </div>
  );
}

export default Statistics;